import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Divider, Typography } from "antd";
import { useSelector } from "react-redux";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartLoader from "./ChartLoader";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const { Title: AntTitle } = Typography;

const EMPTY_CHART_DATA = {
  labels: [],
  datasets: [
    {
      label: "Free Tier",
      data: [],
      borderColor: "#1578f2",
      borderWidth: 1.5,
      tension: 0.4,
    },
    {
      label: "Free Entry Point",
      data: [],
      borderColor: "#00d998",
      borderWidth: 1.5,
      tension: 0.4,
    },
  ],
};

const FreeConversationCharts = () => {
  const { whatsappBusinessInsightsLoading, whatsappBusinessInsights, WhatsappBusinessPricingInsights } =
    useSelector((store) => store.Channel);

  const chartData = useMemo(() => {
    if (!WhatsappBusinessPricingInsights?.length) return null;

    // Group data by date and conversation type
    const groupedData = WhatsappBusinessPricingInsights.reduce((acc, item) => {
      const date = new Date(item.start * 1000).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      const type = item.pricing_type;

      if (!acc[date]) {
        acc[date] = {
          FREE_CUSTOMER_SERVICE: 0,
          FREE_ENTRY_POINT: 0,
        };
      }

      if (type === "FREE_CUSTOMER_SERVICE" || type === "FREE_ENTRY_POINT") {
        acc[date][type] += item.volume;
      }
      return acc;
    }, {});

    // Sort dates
    const sortedDates = Object.keys(groupedData).sort((a, b) => {
      return new Date(a) - new Date(b);
    });

    return {
      labels: ["", ...sortedDates],
      datasets: [
        {
          label: "Free Customer Service",
          data: [null, ...sortedDates.map((date) => groupedData[date].FREE_CUSTOMER_SERVICE)],
          borderColor: "#1578f2",
          backgroundColor: "#1578f2",
          borderWidth: 1.5,
          tension: 0.4,
        },
        {
          label: "Free Entry Point",
          data: [null, ...sortedDates.map((date) => groupedData[date].FREE_ENTRY_POINT)],
          borderColor: "#00d998",
          backgroundColor: "#00d998",
          borderWidth: 1.5,
          tension: 0.4,
        },
      ],
    };
  }, [WhatsappBusinessPricingInsights]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          // callback: (value) => `${Number(value) / 1000}K`,
          stepSize: 1,
          // max: 5000,
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,
          boxWidth: 8,
          boxHeight: 8,
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        position: "nearest",
      },
    },
  };

  return (
    <div className="w-full p-4 bg-white rounded-lg shadow-md">
      <AntTitle level={5} className="text-gray-700 mb-4">
        Free Messages Delivered
      </AntTitle>
      <Divider className="mt-0" />
      <div className="w-[60vw] mx-auto h-96 relative">
        {whatsappBusinessInsightsLoading ? (
          <ChartLoader />
        ) : (
          <>
            <Line data={chartData || EMPTY_CHART_DATA} options={options} />
            {(!chartData ||
              !chartData.labels ||
              chartData.labels.length === 0) && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-white bg-opacity-80">
                <span className="text-gray-500 text-lg font-medium">
                  No data available
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FreeConversationCharts;
